import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { addBranchFilter } from '../utils/branchFilter';
import AdvancedEditModal from './AdvancedEditModal';
import {
  buildDefaultUnitQcReport,
  UNIT_QC_LABELS,
  OLD_MOBILE_GRADE_OPTIONS,
  formatOldMobileGradesSummary,
  gradeBadgeClass
} from '../constants/oldMobileQc';
// Sale logic extracted — import for local use, re-exported for backward compatibility
import { createEmiSale } from '../utils/inventorySaleHelpers';
export { createEmiSale };
import { uploadSalePhoto } from '../utils/uploadSalePhoto';
import { uploadUnitRealVideo } from '../utils/uploadUnitRealVideo';
import BuybackModal from './modals/BuybackModal';
import UnitQcViewerModal from './modals/UnitQcViewerModal';
import VariantModal from './modals/VariantModal';
import PaymentModal from './modals/PaymentModal';
import ImeiManagementModal from './modals/ImeiManagementModal';
import ProductSaleHistoryModal from './modals/ProductSaleHistoryModal';
import StockModal from './modals/StockModal';
import SaleModal from './modals/SaleModal';
import { formatImeiSellerSummary, formatImeiBranchPriceSummary } from '../utils/imeiHelpers';

const DEFAULT_UNIT_QC_REPORT = buildDefaultUnitQcReport();

// formatImeiSellerSummary and formatImeiBranchPriceSummary imported from utils/imeiHelpers

function Inventory() {
  const [products, setProducts] = useState([]);
  const [emiPlans, setEmiPlans] = useState([]);
  const [sales, setSales] = useState([]);
  const [salesByMonth, setSalesByMonth] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalInvestment, setTotalInvestment] = useState(0);
  const [filterType, setFilterType] = useState('all');
  const [monthFilter, setMonthFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [categories, setCategories] = useState([]);
  const [sortBy, setSortBy] = useState('latest');
  const [view, setView] = useState('products'); // 'products', 'emi', or 'sales'
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedEmiId, setSelectedEmiId] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [showStockModal, setShowStockModal] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [stockAmount, setStockAmount] = useState('');
  const [stockLoading, setStockLoading] = useState(false);
  const [imeiList, setImeiList] = useState([]); // For collecting IMEIs when adding stock
  const [showSaleModal, setShowSaleModal] = useState(false);
  const [submittingSale, setSubmittingSale] = useState(false);
  const [salePhotoFile, setSalePhotoFile] = useState(null);
  const [showImeiManagementModal, setShowImeiManagementModal] = useState(false);
  const [selectedImeiProductId, setSelectedImeiProductId] = useState(null);
  const [saleHistoryModal, setSaleHistoryModal] = useState(null);
  const [imeiManagementLoading, setImeiManagementLoading] = useState(false);
  const [unitQcViewer, setUnitQcViewer] = useState(null);
  /** Old Mobile: buy back from last buyer — separate from record sale */
  const [buybackModal, setBuybackModal] = useState(null);
  const [buybackPriceInput, setBuybackPriceInput] = useState('');
  const [buybackNotesInput, setBuybackNotesInput] = useState('');
  const [buybackSubmitting, setBuybackSubmitting] = useState(false);
  const [saleData, setSaleData] = useState({
    quantity: 1,
    salePrice: '',
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    date: new Date().toISOString().substr(0, 10),
    isEmi: false,
    emiDetails: {
      months: 3,
      interestRate: '',
      downPayment: '',
      preferredPaymentDate: 1
    },
    // Add witness information fields
    witness: {
      name: '',
      phone: '',
      id: null
    },
    // Add reward points usage
    useRewardPoints: false,
    rewardPointsToUse: 0,
    // Add IMEI selection
    selectedImeiIndex: null
  });
  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ email: '', address: '', idProof: '' });
  const [emiSearchTerm, setEmiSearchTerm] = useState('');
  const [emiSearchResults, setEmiSearchResults] = useState([]);
  const [isEmiSearching, setIsEmiSearching] = useState(false);
  
  // Add product search state
  const [productSearchTerm, setProductSearchTerm] = useState('');

  // Witness states
  const [selectedWitness, setSelectedWitness] = useState(null);
  const [isSelfWitness, setIsSelfWitness] = useState(false);
  const [witnessCustomers, setWitnessCustomers] = useState([]);
  const [witnessSearchTerm, setWitnessSearchTerm] = useState('');
  const [witnessSearchResults, setWitnessSearchResults] = useState([]);
  const [isWitnessSearching, setIsWitnessSearching] = useState(false);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editProductData, setEditProductData] = useState(null);
  const [editLoading, setEditLoading] = useState(false);

  // Variant management states
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [selectedVariantProductId, setSelectedVariantProductId] = useState(null);
  const [variantData, setVariantData] = useState({
    variantName: 'RAM/Storage',
    variantValue: '',
    price: 0,
    buyingPrice: 0,
    stock: 1,
    offerPrice: 0,
    ramGB: 0,
    storageGB: 0,
    color: ''
  });
  const [variantImeiList, setVariantImeiList] = useState([{ imei1: '', imei2: '' }]);
  const [variantLoading, setVariantLoading] = useState(false);

  const availableMonths = useMemo(() => {
    const months = [];
    const today = new Date();
    
    // Loop to get the last 3 months including the current one
    for (let i = 0; i < 3; i++) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleString('default', { month: 'long', year: 'numeric' });
      months.push({ key: monthKey, value: monthKey, label: label });
    }
    return months;
  }, []); // This runs only once as it's based on the current date

  useEffect(() => {
    // Break down fetching into separate, independently failing functions
    fetchProducts();
    fetchEmiData();
    fetchSales();

  }, []);

  // Load approved witness customers
  useEffect(() => {
    const loadWitnessCustomers = async () => {
      try {
        const response = await axios.get(`${config.API_URL}/customers/witnesses`);
        setWitnessCustomers(response.data);
      } catch (error) {
        console.error('Error loading witness customers:', error);
      }
    };
    loadWitnessCustomers();
  }, []);

  // Search witnesses function
  const searchWitnesses = async (query) => {
    if (!query || query.length < 2) {
      setWitnessSearchResults([]);
      return;
    }

    setIsWitnessSearching(true);
    try {
      const response = await axios.get(`${config.API_URL}/customers/search/witness/${encodeURIComponent(query)}`);
      setWitnessSearchResults(response.data);
    } catch (error) {
      console.error('Error searching witnesses:', error);
      setWitnessSearchResults([]);
    } finally {
      setIsWitnessSearching(false);
    }
  };

  // Clear witness search
  const clearWitnessSearch = () => {
    setWitnessSearchTerm('');
    setWitnessSearchResults([]);
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${config.API_URL}/products`, {
        params: addBranchFilter({ limit: 2000 })
      });
      const rows = Array.isArray(response.data) ? response.data : [];
      setProducts(rows);
      setTotalInvestment(
        rows.reduce((sum, product) => sum + (product.buyingPrice || 0) * (product.stock || 0), 0)
      );
      // Get unique categories from products
      const uniqueCategories = [...new Set(rows.map(p => p.category))];
      setCategories(uniqueCategories);
    } catch (err) {
      setError('Failed to fetch products. Some data may be missing.');
      console.error("Error fetching products:", err);
    } finally {
      setLoading(false); // Stop loading indicator after products are fetched
    }
  };

  const fetchEmiData = async () => {
    try {
      const response = await axios.get(`${config.API_URL}/emi`);
      setEmiPlans(response.data);
    } catch (err) {
      // Don't set a blocking error, just log it. The page can function without EMIs.
      console.error("Failed to fetch EMI data:", err);
      // Optionally set a non-blocking warning for the user
      // setError('Could not load EMI data. EMI-related features might be unavailable.');
    }
  };

  const fetchSales = async () => {
    try {
      const response = await axios.get(`${config.API_URL}/sales`);
      setSales(response.data.allSales);
      setSalesByMonth(response.data.salesByMonth);
    } catch (err) {
      console.error("Failed to fetch sales data:", err);
    }
  };

  // Backward-compat helper for legacy calls
  const fetchData = async () => {
    await Promise.allSettled([
      fetchProducts(),
      fetchEmiData(),
      fetchSales()
    ]);
  };

  const calculateTotalInvestment = () => {
    return products.reduce((total, product) => total + (product.buyingPrice || 0) * (product.stock || 0), 0);
  };

  // Get current month and year
  const getCurrentMonthYear = () => {
    const date = new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  };

  // Helper function to get category icon
  const getCategoryIcon = (category) => {
    const iconMap = {
      'Mobile': '📱',
      'Old Mobile': '📲',
      'Speaker': '🔊',
      'Home Theatre': '🎬',
      'Soundbar': '📢',
      'Neckband': '🎧',
      'Airpods': '🎵',
      'Smartwatches': '⌚',
      'TV': '📺',
      'Refrigerator': '❄️',
      'Washing Machine': '🧺',
      'AC': '❄️',
      'FAN': '💨',
      'Microwave': '🔥',
      'Mixer/Juicer': '🥤',
      'Other Accessories': '🔧'
    };
    return iconMap[category] || '📦';
  };

  // Filter products by criteria
  const getFilteredProducts = () => {
    let filtered = [...products];
    
    // Search filter
    if (productSearchTerm.trim()) {
      const searchTerm = productSearchTerm.toLowerCase().trim();
      filtered = filtered.filter(product => 
        product.name.toLowerCase().includes(searchTerm) ||
        product.category.toLowerCase().includes(searchTerm) ||
        (product.description && product.description.toLowerCase().includes(searchTerm))
      );
    }
    
    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(product => product.category === categoryFilter);
    }
    
    // Stock status filter
    if (filterType === 'low') {
      filtered = filtered.filter(product => (product.stock || 0) <= 3);
    } else if (filterType === 'medium') {
      filtered = filtered.filter(product => (product.stock || 0) > 3 && (product.stock || 0) <= 10);
    } else if (filterType === 'high') {
      filtered = filtered.filter(product => (product.stock || 0) > 10);
    }
    
    // Sort products
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'latest':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'oldest':
          return new Date(a.createdAt) - new Date(b.createdAt);
        case 'priceLowToHigh':
          return (a.price || 0) - (b.price || 0);
        case 'priceHighToLow':
          return (b.price || 0) - (a.price || 0);
        case 'stockLowToHigh':
          return (a.stock || 0) - (b.stock || 0);
        case 'stockHighToLow':
          return (b.stock || 0) - (a.stock || 0);
        case 'nameAZ':
          return a.name.localeCompare(b.name);
        case 'nameZA':
          return b.name.localeCompare(a.name);
        default:
          return 0;
      }
    });
    
    return filtered;
  };

  // Filter EMI plans
  const getFilteredEmiPlans = () => {
    let filtered = [...emiPlans];
    
    // Filter by customer search term if provided
    if (emiSearchTerm) {
      filtered = filtered.filter(emi => 
        emi.customer.name.toLowerCase().includes(emiSearchTerm.toLowerCase()) || 
        emi.customer.phone.includes(emiSearchTerm)
      );
    }
    
    // Filter by active/pending status
    if (filterType === 'pending') {
      filtered = filtered.filter(emi => emi.status === 'active');
    } else if (filterType === 'completed') {
      filtered = filtered.filter(emi => emi.status === 'completed');
    } else if (filterType === 'defaulted') {
      filtered = filtered.filter(emi => emi.status === 'defaulted');
    }
    
    // Filter by month if selected
    if (monthFilter !== 'all') {
      filtered = filtered.filter(emi => {
        const emiDate = new Date(emi.startDate);
        const emiMonthYear = `${emiDate.getFullYear()}-${String(emiDate.getMonth() + 1).padStart(2, '0')}`;
        return emiMonthYear === monthFilter;
      });
    }
    
    return filtered;
  };

  // Filter sales by month
  const getFilteredSales = () => {
    if (!salesByMonth || typeof salesByMonth !== 'object') {
      return [];
    }

    // Convert the salesByMonth object to an array of objects
    const monthArray = Object.keys(salesByMonth).map(monthKey => ({
      month: monthKey,
      ...salesByMonth[monthKey]
    }));
    
    // Sort by date, most recent first — show last 6 months only
    monthArray.sort((a, b) => new Date(b.month) - new Date(a.month));

    const lastSixMonths = monthArray.slice(0, 6);

    if (monthFilter === 'all') {
      return lastSixMonths;
    }
    
    return lastSixMonths.filter(monthData => monthData.month === monthFilter);
  };

  // Calculate late fee for EMI payments
  const calculateLateFee = (emi) => {
    if (!emi) return 0;
    
    const currentDate = new Date();
    const startDate = new Date(emi.startDate);
    const monthsPassed = (currentDate.getFullYear() - startDate.getFullYear()) * 12 + 
                         (currentDate.getMonth() - startDate.getMonth());
    
    const expectedPayments = Math.min(monthsPassed + 1, emi.emiPlan.months); // +1 for down payment
    const actualPayments = emi.payments.filter(payment => payment.status === 'completed').length;
    
    // If fewer payments made than expected, calculate late fee (10% of monthly amount)
    if (actualPayments < expectedPayments) {
      const missedPayments = expectedPayments - actualPayments;
      return missedPayments * (emi.emiPlan.monthlyAmount * 0.1);
    }
    
    return 0;
  };

  // Calculate total pending EMI amount
  const calculatePendingEmiAmount = () => {
    return emiPlans
      .filter(emi => emi.status === 'active')
      .reduce((total, emi) => {
        const totalPaid = emi.payments.reduce((sum, payment) => 
          payment.status === 'completed' ? sum + payment.amount : sum, 0);
        const pending = emi.emiPlan.totalAmount - totalPaid;
        const lateFee = calculateLateFee(emi);
        return total + pending + lateFee;
      }, 0);
  };

  // Calculate total this month's expected EMI collections
  const calculateThisMonthEMI = () => {
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    
    return emiPlans
      .filter(emi => emi.status === 'active')
      .reduce((total, emi) => {
        const startDate = new Date(emi.startDate);
        const monthsPassed = (currentYear - startDate.getFullYear()) * 12 + 
                            (currentMonth - startDate.getMonth());
        
        // If we're within the EMI period and payment for this month isn't made yet
        if (monthsPassed < emi.emiPlan.months) {
          const paymentsThisMonth = emi.payments.filter(payment => {
            const paymentDate = new Date(payment.date);
            return paymentDate.getMonth() === currentMonth && 
                  paymentDate.getFullYear() === currentYear;
          });
          
          if (paymentsThisMonth.length === 0) {
            return total + emi.emiPlan.monthlyAmount;
          }
        }
        
        return total;
      }, 0);
  };

  // Add function to calculate profit for a sale
  const calculateProfit = (sale, buyingPrice) => {
    if (!sale || !buyingPrice) return 0;
    
    return sale.salePrice - buyingPrice;
  };

  // Add function to calculate profit for an EMI plan
  const calculateEmiProfit = (emi) => {
    if (!emi || !emi.productInfo) return 0;
    
    const buyingPrice = emi.productInfo.buyingPrice || 0;
    const sellingPrice = emi.saleInfo?.salePrice || emi.productInfo.sellingPrice || 0;
    
    return sellingPrice - buyingPrice;
  };

  // Add function to calculate reward points (10% of profit)
  const calculateRewardPoints = (profit) => {
    return Math.round(profit * 0.1);
  };


  // Handler for recording EMI payment
  const handleRecordPayment = async (emiId) => {
    setSelectedEmiId(emiId);
    setPaymentAmount('');
    setShowPaymentModal(true);
  };

  // Submit EMI payment
  const submitPayment = async () => {
    if (!selectedEmiId) return;
    
    setPaymentLoading(true);
    try {
      const amount = parseFloat(paymentAmount);
      if (isNaN(amount) || amount <= 0) {
        throw new Error('Please enter a valid payment amount');
      }
      
      await axios.post(`${config.API_URL}/emi/${selectedEmiId}/payment`, { amount });
      
      // Refresh EMI plans
      const emiResponse = await axios.get(`${config.API_URL}/emi`);
      setEmiPlans(emiResponse.data || []);
      
      setShowPaymentModal(false);
      setSelectedEmiId(null);
      setPaymentAmount('');
      alert('Payment recorded successfully');
    } catch (err) {
      setError('Failed to record payment: ' + err.message);
      console.error('Error recording payment:', err);
    } finally {
      setPaymentLoading(false);
    }
  };

  // Handler for incrementing stock
  const handleIncrementStock = (productId, currentPrice) => {
    setSelectedProductId(productId);
    setStockAmount('');
    setShowStockModal(true);
  };

  // Submit stock increment
  const submitStockIncrement = async () => {
    if (!selectedProductId) return;
    
    setStockLoading(true);
    try {
      const product = products.find(p => p._id === selectedProductId);
      const isMobile = product && (product.category === 'Mobile' || product.category === 'Old Mobile');
      
      if (isMobile) {
        // For mobile devices, validate IMEIs
        const validImeis = imeiList.filter(imei => imei.imei1 && imei.imei1.length === 15);
        if (validImeis.length === 0) {
          throw new Error('Please enter at least one valid IMEI');
        }

        const isOldMobile = product.category === 'Old Mobile';

        if (isOldMobile) {
          for (const row of validImeis) {
            const sp = String(row.sellerPhone || '').replace(/\D/g, '');
            if (sp.length > 0 && sp.length < 10) {
              throw new Error('Seller mobile diya hai to 10 digit complete likhein');
            }
            if (!String(row.unitConditionGrade || '').trim()) {
              throw new Error('Har unit ke liye condition grade select karein (A+ … D)');
            }
          }
        }

        for (const imei of validImeis) {
          if (isOldMobile) {
            const payload = {
              imei1: imei.imei1,
              imei2: imei.imei2 || '',
              unitSeller: {
                sellerName: String(imei.sellerName || '').trim(),
                sellerPhone: String(imei.sellerPhone || '').trim()
              },
              unitQcReport: imei.unitQcReport && typeof imei.unitQcReport === 'object'
                ? imei.unitQcReport
                : { ...DEFAULT_UNIT_QC_REPORT },
              unitConditionGrade: String(imei.unitConditionGrade || '').trim()
            };
            if (String(imei.serialNo || '').trim()) payload.serialNo = String(imei.serialNo).trim();
            const addRes = await axios.post(`${config.API_URL}/products/${selectedProductId}/imei`, payload);
            const imeiIndex = addRes.data.imeiIndex;
            const files = imei.mediaMode === 'photos' ? (imei.photoFiles || []) : [];
            if (files.length > 0 && imeiIndex != null) {
              const fd = new FormData();
              files.slice(0, 3).forEach((f) => fd.append('photos', f));
              await axios.post(
                `${config.API_URL}/products/${selectedProductId}/imei/${imeiIndex}/unit-real-photos`,
                fd,
                { headers: { 'Content-Type': 'multipart/form-data' } }
              );
            }
            if (imei.mediaMode === 'video' && imei.videoFile && imeiIndex != null) {
              const vr = await uploadUnitRealVideo(imei.videoFile, {
                productId: selectedProductId,
                imeiIndex
              });
              if (!vr.success && !vr.skipped) {
                throw new Error(vr.error || 'Unit video upload failed');
              }
            }
            if (imei.sellerIntakePhoto && imeiIndex != null) {
              let sellerCustomerId = '';
              const ph = String(imei.sellerPhone || '').replace(/\D/g, '').slice(-10);
              if (ph.length === 10) {
                try {
                  const cr = await axios.get(`${config.API_URL}/customers`, { params: { phone: ph } });
                  if (cr.data?.customer?._id) sellerCustomerId = cr.data.customer._id;
                } catch (_) {
                  /* customer may be created async with intake reward */
                }
              }
              await uploadSalePhoto(imei.sellerIntakePhoto, {
                kind: 'seller_intake',
                productId: selectedProductId,
                imeiIndex,
                customerId: sellerCustomerId
              });
            }
          } else {
            const payload = {
              imei1: imei.imei1,
              imei2: imei.imei2 || ''
            };
            if (String(imei.serialNo || '').trim()) payload.serialNo = String(imei.serialNo).trim();
            const supplierName = String(imei.sellerName || '').trim();
            if (supplierName) {
              payload.unitSeller = { sellerName: supplierName, sellerPhone: '' };
            }
            await axios.post(`${config.API_URL}/products/${selectedProductId}/imei`, payload);
          }
        }

        alert(`Successfully added ${validImeis.length} IMEI(s) to stock`);
      } else {
        // For non-mobile products, use regular stock increment
      const quantity = parseInt(stockAmount);
      if (isNaN(quantity) || quantity <= 0) {
        throw new Error('Please enter a valid quantity');
      }
      
      await axios.patch(`${config.API_URL}/products/${selectedProductId}/increment-stock`, { quantity });
        alert('Stock updated successfully');
      }
      
      // Refresh products
      const productsResponse = await axios.get(`${config.API_URL}/products`, {
        params: addBranchFilter({ limit: 2000 })
      });
      const rows = Array.isArray(productsResponse.data) ? productsResponse.data : [];
      setProducts(rows);
      setTotalInvestment(
        rows.reduce((sum, product) => sum + (product.buyingPrice || 0) * (product.stock || 0), 0)
      );
      
      setShowStockModal(false);
      setSelectedProductId(null);
      setStockAmount('');
      setImeiList([]);
    } catch (err) {
      setError('Failed to update stock: ' + err.message);
      console.error('Error updating stock:', err);
    } finally {
      setStockLoading(false);
    }
  };

  // Handler for recording a sale (optional repurchasePrefill from sold IMEI: same customer + last price)
  const handleRecordSale = (productId, price, repurchasePrefill = null) => {
    setSelectedProductId(productId);
    const basePrice = Number(price) || 0;
    const fromLastSale =
      repurchasePrefill &&
      repurchasePrefill.salePrice != null &&
      !Number.isNaN(Number(repurchasePrefill.salePrice));
    const initialSalePrice = fromLastSale ? Number(repurchasePrefill.salePrice) : basePrice;
    const downPayment = Math.round(initialSalePrice * 0.4); // 40% down payment

    setSaleData({
      ...saleData,
      salePrice: initialSalePrice,
      customerName: repurchasePrefill?.customerName ?? '',
      customerPhone: repurchasePrefill?.customerPhone ?? '',
      selectedImeiIndex: null,
      useRewardPoints: false,
      rewardPointsToUse: 0,
      emiDetails: {
        ...saleData.emiDetails,
        downPayment: downPayment.toString()
      }
    });
    setShowSaleModal(true);
    setSelectedCustomer(null);
    setCustomerSearchResults([]);
    setShowNewCustomerForm(false);

    const cid = repurchasePrefill?.customerId;
    const phone = repurchasePrefill?.customerPhone;
    if (cid) {
      (async () => {
        try {
          const r = await axios.get(`${config.API_URL}/customers/${cid}`);
          if (r.data) setSelectedCustomer(r.data);
        } catch (e) {
          console.warn('Repurchase customer prefetch:', e.message);
        }
      })();
    } else if (phone && String(phone).trim().length >= 3) {
      (async () => {
        try {
          const r = await axios.get(
            `${config.API_URL}/customers/search/phone/${encodeURIComponent(phone)}`
          );
          if (Array.isArray(r.data) && r.data[0]) setSelectedCustomer(r.data[0]);
        } catch (e) {
          console.warn('Repurchase customer prefetch:', e.message);
        }
      })();
    }
  };

  const handleOpenImeiManagement = async (productId) => {
    const product = products.find((p) => p._id === productId);
    if (
      product?.category === 'Old Mobile' &&
      String(product?.acquiredFrom?.sellerName || '').trim() &&
      (product.imeiTracking || []).some(
        (r) => r.status === 'available' && !formatImeiSellerSummary(product, r)
      )
    ) {
      try {
        const res = await axios.post(
          `${config.API_URL}/products/${productId}/sync-acquisition-to-unit-sellers`
        );
        const refreshed = res.data?.product;
        if (refreshed) {
          setProducts((prev) => prev.map((p) => (String(p._id) === String(productId) ? refreshed : p)));
        }
      } catch (e) {
        console.warn('sync-acquisition-to-unit-sellers:', e.response?.data?.message || e.message);
      }
    }
    setSelectedImeiProductId(productId);
    setShowImeiManagementModal(true);
  };

  const openOldMobileBuyback = (product, imeiRow) => {
    const imeiIndex = product.imeiTracking.findIndex((t) => t.imei1 === imeiRow.imei1);
    if (imeiIndex < 0) {
      alert('IMEI row not found');
      return;
    }
    setShowImeiManagementModal(false);
    setSelectedImeiProductId(null);
    const st = imeiRow.soldTo || {};
    setBuybackModal({
      productId: product._id,
      productName: product.name,
      imeiIndex,
      imei1: imeiRow.imei1,
      imei2: imeiRow.imei2 || '',
      customerName: st.customerName || '',
      customerPhone: st.customerPhone || '',
      lastSalePrice: st.salePrice != null ? Number(st.salePrice) : null,
      hasEmi: !!st.emiId
    });
    setBuybackPriceInput(
      st.salePrice != null && !Number.isNaN(Number(st.salePrice)) ? String(Math.round(Number(st.salePrice))) : ''
    );
    setBuybackNotesInput('');
  };

  const submitOldMobileBuyback = async () => {
    if (!buybackModal) return;
    const price = parseFloat(buybackPriceInput);
    if (!Number.isFinite(price) || price <= 0) {
      alert('Valid buyback amount (₹) enter karein');
      return;
    }
    setBuybackSubmitting(true);
    try {
      await axios.patch(
        `${config.API_URL}/products/${buybackModal.productId}/imei/${buybackModal.imeiIndex}/buyback`,
        { buybackPrice: price, notes: buybackNotesInput.trim() }
      );
      await fetchProducts();
      setBuybackModal(null);
      setBuybackPriceInput('');
      setBuybackNotesInput('');
      alert('Buyback ho gaya — unit dubara stock mein hai. Purani sale history save ho gayi hai.');
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Buyback failed');
    } finally {
      setBuybackSubmitting(false);
    }
  };

  const handleMarkImeiAsSold = async (productId, imeiIndex, reason = 'Manual adjustment') => {
    try {
      setImeiManagementLoading(true);
      const response = await axios.patch(`${config.API_URL}/products/${productId}/imei/${imeiIndex}/manual-sold`, {
        reason,
        notes: `IMEI manually marked as sold - ${new Date().toLocaleString()}`
      });

      if (response.data.success) {
        await fetchProducts();
        alert('IMEI marked as sold successfully!');
      }
    } catch (error) {
      console.error('Error marking IMEI as sold:', error);
      alert('Failed to mark IMEI as sold: ' + (error.response?.data?.message || error.message));
    } finally {
      setImeiManagementLoading(false);
    }
  };

  const handleBulkMarkImeisAsSold = async (productId, imeiIndexes, reason = 'Bulk manual adjustment') => {
    try {
      setImeiManagementLoading(true);
      const response = await axios.patch(`${config.API_URL}/products/${productId}/imeis/bulk-manual-sold`, {
        imeiIndexes,
        reason,
        notes: `Multiple IMEIs manually marked as sold - ${new Date().toLocaleString()}`
      });

      if (response.data.success) {
        await fetchProducts();
        alert(`${response.data.successCount} IMEIs marked as sold successfully!`);
      }
    } catch (error) {
      console.error('Error marking IMEIs as sold:', error);
      alert('Failed to mark IMEIs as sold: ' + (error.response?.data?.message || error.message));
    } finally {
      setImeiManagementLoading(false);
    }
  };

  // Search for customers by phone number
  const searchCustomerByPhone = async () => {
    if (!saleData.customerPhone || saleData.customerPhone.length < 3) {
      setCustomerSearchResults([]);
      return;
    }
    
    try {
      setCustomerSearchLoading(true);
      setError(null); // Clear any previous errors
      console.log('Searching for customer with phone:', saleData.customerPhone);
      
      const response = await axios.get(`${config.API_URL}/customers/search/phone/${saleData.customerPhone}`);
      
      console.log('Customer search results:', response.data);
      
      if (response.data && response.data.length > 0) {
        setCustomerSearchResults(response.data);
      } else {
        setCustomerSearchResults([]);
        // No error, just no results found
      }
      setCustomerSearchLoading(false);
    } catch (err) {
      console.error('Error searching for customer:', err);
      setCustomerSearchLoading(false);
      setCustomerSearchResults([]);
      setError('Failed to search for customer: ' + (err.response?.data?.message || err.message));
    }
  };

  // Handle customer selection
  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setSaleData({
      ...saleData,
      customerName: customer.name,
      customerPhone: customer.phone,
      customerEmail: customer.email || ''
    });
    setCustomerSearchResults([]);
  };

  // Handle new customer form changes
  const handleNewCustomerChange = (e) => {
    const { name, value } = e.target;
    setNewCustomer({ ...newCustomer, [name]: value });
  };

  // Find or create customer
  const findOrCreateCustomer = async () => {
    try {
      // If customer is already selected, return it
      if (selectedCustomer) {
        return selectedCustomer;
      }
      
      // If we have customer phone, try to find or create
      if (saleData.customerPhone) {
        setError(null); // Clear any previous errors
        
        // Validate phone number
        if (saleData.customerPhone.length < 3) {
          throw new Error('Please enter a valid phone number (at least 3 digits)');
        }
        
        // Validate name if creating a new customer
        if (!saleData.customerName) {
          throw new Error('Please enter a customer name');
        }
        
        const response = await axios.post(`${config.API_URL}/customers/find-or-create`, {
          phone: saleData.customerPhone,
          name: saleData.customerName || newCustomer.name,
          email: saleData.customerEmail || newCustomer.email,
          address: newCustomer.address,
          idProof: newCustomer.idProof
        });
        
        // If similar customers found, show them
        if (response.data.similar) {
          setCustomerSearchResults(response.data.customers);
          throw new Error('Similar customers found. Please select one or create a new customer.');
        }
        
        return response.data.customer;
      }
      
      // If no customer info provided
      return null;
    } catch (err) {
      console.error('Error finding/creating customer:', err);
      throw err;
    }
  };



  // Submit sale record
  const submitSaleRecord = async () => {
    if (!selectedProductId) return;
    
    // Prevent multiple submissions
    if (submittingSale) {
      console.warn('Sale submission already in progress');
      return;
    }
    
    // Clear any previous error
    setError(null);
    
    // Set submission state to true to prevent duplicate submissions
    setSubmittingSale(true);
    
    try {
      console.log('Recording sale for product:', selectedProductId);
      
      // Prepare the sale data with witness information and reward points
      const saleDataWithWitness = {
        ...saleData,
        witness: saleData.isEmi ? (isSelfWitness ? {
          id: selectedCustomer?._id,
          name: selectedCustomer?.name,
          phone: selectedCustomer?.phone
        } : selectedWitness ? {
          id: selectedWitness._id,
          name: selectedWitness.name,
          phone: selectedWitness.phone
        } : undefined) : undefined, // No witness for cash sales
        isEmi: saleData.isEmi, // Explicitly pass isEmi flag
        useRewardPoints: saleData.useRewardPoints,
        rewardPointsToUse: saleData.rewardPointsToUse,
        customerId: selectedCustomer?._id
      };
      
      const result = await createEmiSale(
        selectedProductId, 
        saleDataWithWitness, 
        selectedCustomer, 
        newCustomer
      );

      if (!result.success) {
        if (result.similarCustomers) {
          setCustomerSearchResults(result.similarCustomers);
          setError('Similar customers found. Please select one or create a new customer.');
          return;
        }
        setError('Failed to record sale: ' + result.error);
        return;
      }

      const buyerId = result.customerId || selectedCustomer?._id || saleDataWithWitness.customerId;
      if (salePhotoFile && buyerId) {
        const photoRes = await uploadSalePhoto(salePhotoFile, {
          customerId: buyerId,
          kind: 'customer_purchase',
          emiId: result.emiId || '',
          productId: result.productId || selectedProductId,
          productSaleId: result.productSaleId || ''
        });
        if (!photoRes.success) {
          console.warn('Sale photo upload:', photoRes.error);
        }
      }
      
      // Refresh data
      const productsResponse = await axios.get(`${config.API_URL}/products`);
      setProducts(productsResponse.data || []);
      
      // Refresh sales data
      const salesResponse = await axios.get(`${config.API_URL}/products/sales/all`);
      setSales(salesResponse.data || []);
      
      // Refresh sales by month
      const salesByMonthResponse = await axios.get(`${config.API_URL}/products/sales/by-month`);
      setSalesByMonth(salesByMonthResponse.data || []);
      
      // Refresh EMI plans
      const emiResponse = await axios.get(`${config.API_URL}/emi`);
      setEmiPlans(emiResponse.data || []);
      
      setShowSaleModal(false);
      setSelectedProductId(null);
      setSelectedCustomer(null);
      setSelectedWitness(null);
      setIsSelfWitness(false);
      setSalePhotoFile(null);

      setSaleData({
        quantity: 1,
        salePrice: '',
        customerName: '',
        customerPhone: '',
        customerEmail: '',
        date: new Date().toISOString().substr(0, 10),
        isEmi: false,
        useRewardPoints: false,
        rewardPointsToUse: 0,
        selectedImeiIndex: null,
        emiDetails: {
          months: 3,
          interestRate: '',
          downPayment: '',
          preferredPaymentDate: 1
        },

      });
      setNewCustomer({
        email: '',
        address: '',
        idProof: ''
      });
    } catch (err) {
      console.error('Error recording sale:', err);
      setError('Failed to record sale: ' + (err.response?.data?.message || err.message));
    } finally {
      setSubmittingSale(false);
    }
  };

  // Calculate EMI details for display
  const calculateEmiDetails = () => {
    const salePrice = parseFloat(saleData.salePrice) || 0;
    const downPayment = parseFloat(saleData.emiDetails.downPayment) || 0;
    
    if (salePrice <= 0 || downPayment <= 0 || downPayment >= salePrice) {
      return {
        principal: 0,
        interest: 0,
        totalAmount: 0,
        monthlyAmount: 0,
        totalWithDownPayment: 0
      };
    }
    
    const principal = salePrice - downPayment;
    // Handle empty or zero interest rate
    const interestRate = saleData.emiDetails.interestRate ? parseFloat(saleData.emiDetails.interestRate) : 0;
    const interest = (principal * interestRate * saleData.emiDetails.months) / 100;
    const totalAmount = principal + interest;
    const monthlyAmount = totalAmount / saleData.emiDetails.months;
    
    return {
      principal,
      interest,
      totalAmount,
      monthlyAmount,
      totalWithDownPayment: totalAmount + downPayment
    };
  };

  // Add ordinal suffix helper function
  const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  // Handle EMI search
  const handleEmiSearch = async (e) => {
    e.preventDefault();
    
    if (!emiSearchTerm.trim()) {
      return;
    }
    
    setIsEmiSearching(true);
    try {
      // Search by phone in API (if term looks like phone number)
      if (/^\d{4,}$/.test(emiSearchTerm)) {
        const response = await axios.get(`${config.API_URL}/emi/customer/${emiSearchTerm}`);
        if (response.data && response.data.length > 0) {
          setEmiSearchResults(response.data);
          setEmiPlans(prevPlans => {
            // Merge with existing plans to avoid duplicates
            const allPlans = [...prevPlans];
            response.data.forEach(newPlan => {
              if (!allPlans.find(plan => plan._id === newPlan._id)) {
                allPlans.push(newPlan);
              }
            });
            return allPlans;
          });
        }
      }
    } catch (err) {
      console.error('Error searching for EMI plans:', err);
      // We'll still filter the existing plans by search term
    } finally {
      setIsEmiSearching(false);
    }
  };

  // Clear EMI search
  const clearEmiSearch = () => {
    setEmiSearchTerm('');
    setEmiSearchResults([]);
  };

  // Delete sale function
  const deleteSale = async (saleId) => {
    if (!window.confirm('Are you sure you want to delete this sale? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await axios.delete(`${config.API_URL}/products/sales/${saleId}`);
      // Refresh data after deletion
      fetchData();
      alert(`Sale deleted successfully! Stock restored: ${response.data.restoredStock} units`);
    } catch (err) {
      console.error('Error deleting sale:', err);
      alert('Failed to delete sale: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleEditProduct = (product) => {
    setEditProductData(product);
    setShowEditModal(true);
  };

  const handleUpdateProduct = async (updatedData) => {
    const dataToUpdate = updatedData || editProductData;
    if (!dataToUpdate) return;

    setEditLoading(true);
    try {
      // Prepare comprehensive update data
      const updateData = {
        name: dataToUpdate.name,
        description: dataToUpdate.description,
        price: dataToUpdate.price,
        buyingPrice: dataToUpdate.buyingPrice,
        offerPrice: dataToUpdate.offerPrice,
        stock: dataToUpdate.stock,
        category: dataToUpdate.category,
        brand: dataToUpdate.brand,
        imageUrl: dataToUpdate.imageUrl,
        specs: dataToUpdate.specs || {},
        tags: dataToUpdate.tags || [],
        features: dataToUpdate.features || [],
        emiAvailable: dataToUpdate.emiAvailable,
        ...(dataToUpdate.category === 'Old Mobile' &&
        dataToUpdate.oldMobileRealPhotos !== undefined
          ? { oldMobileRealPhotos: dataToUpdate.oldMobileRealPhotos }
          : {}),
        ...(dataToUpdate.category === 'Old Mobile' &&
        dataToUpdate.acquiredFrom !== undefined
          ? { acquiredFrom: dataToUpdate.acquiredFrom }
          : {}),
        ...(dataToUpdate.procurement !== undefined ? { procurement: dataToUpdate.procurement } : {})
      };

      console.log('Updating product with comprehensive data:', updateData);
      
      const response = await axios.put(`${config.API_URL}/products/${dataToUpdate._id}`, updateData);
      
      console.log('Update response:', response.data);
      
      setShowEditModal(false);
      setEditLoading(false);
      fetchData(); // Refresh data
      alert('Product updated successfully with all specifications! 🎉');
    } catch (err) {
      setEditLoading(false);
      console.error('Error updating product:', err);
      console.error('Error details:', err.response?.data);
      alert(`Failed to update product: ${err.response?.data?.message || err.message}`);
    }
  };

  // Variant management handlers
  const handleAddVariant = (product) => {
    setSelectedVariantProductId(product._id);
    setVariantData({
      variantName: 'RAM/Storage',
      variantValue: '',
      price: product.price,
      buyingPrice: product.buyingPrice,
      stock: 1,
      offerPrice: product.offerPrice,
      ramGB: product.specs?.ramGB || 0,
      storageGB: product.specs?.storageGB || 0,
      color: product.specs?.color || ''
    });
    setVariantImeiList([{ imei1: '', imei2: '' }]);
    setShowVariantModal(true);
  };

  const handleAddVariantSubmit = async () => {
    if (!selectedVariantProductId || !variantData.variantValue) {
      alert('Please fill in variant value');
      return;
    }

    setVariantLoading(true);
    try {
      // Add the variant as a new separate product
      const response = await axios.post(`${config.API_URL}/products/${selectedVariantProductId}/variants`, variantData);

      console.log('Variant added as new product successfully:', response.data);
      setShowVariantModal(false);
      setVariantLoading(false);
      fetchData(); // Refresh main product list
      alert('Product variant added as new product successfully! 🎉');
    } catch (err) {
      setVariantLoading(false);
      console.error('Error adding variant:', err);
      alert(`Failed to add variant: ${err.response?.data?.message || err.message}`);
    }
  };

  // Load witness customers


  // Simple handleSale function (no witness UI)
  const handleSale = async (product, quantity = 1) => {
    try {
      setLoading(true);
      
      // Create sale record
      const saleData = {
        productId: product._id,
        quantity: quantity,
        salePrice: product.salePrice * quantity,
        customerId: selectedCustomer
      };

      const response = await axios.post(`${config.API_URL}/sales`, saleData);
      
      if (response.data.success) {
        // Send purchase confirmation (witness logic handled in backend)
        try {
          await axios.post(`${config.API_URL}/customers/${selectedCustomer}/purchases`, {
            productId: product._id,
            amount: product.salePrice * quantity,
            quantity: quantity
          });
          
          console.log('Purchase confirmation sent');
        } catch (notificationError) {
          console.error('Notification error:', notificationError);
        }

        setSales(prev => [...prev, response.data.sale]);
        setSelectedCustomer('');
        setSelectedProduct(null);
        alert('Sale recorded successfully!');
      }
    } catch (error) {
      console.error('Error recording sale:', error);
      alert('Error recording sale');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid mt-4">
      <style jsx>{`
        .sales-chart {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          height: 200px;
          margin-bottom: 20px;
        }
        
        .chart-column {
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 14%;
        }
        
        .chart-bars {
          display: flex;
          width: 100%;
          height: 180px;
          justify-content: center;
          align-items: flex-end;
        }
        
        .revenue-bar {
          width: 20px;
          background-color: #0d6efd;
          margin-right: 4px;
          border-top-left-radius: 4px;
          border-top-right-radius: 4px;
        }
        
        .profit-bar {
          width: 20px;
          background-color: #198754;
          border-top-left-radius: 4px;
          border-top-right-radius: 4px;
        }
        
        .chart-label {
          text-align: center;
          margin-top: 5px;
          font-size: 12px;
        }
        
        .chart-legend {
          display: flex;
          justify-content: center;
          margin-top: 10px;
        }
        
        .legend-item {
          display: flex;
          align-items: center;
          margin: 0 10px;
        }
        
        .legend-color {
          display: inline-block;
          width: 15px;
          height: 15px;
          margin-right: 5px;
        }
        
        .revenue-color {
          background-color: #0d6efd;
        }
        
        .profit-color {
          background-color: #198754;
        }
      `}</style>
      
      <h2 className="mb-3">Inventory Management</h2>
      
      {/* Summary Cards */}
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="card bg-primary text-white">
            <div className="card-body">
              <h5 className="card-title">Total Products</h5>
              <h3>{products.length}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-success text-white">
            <div className="card-body">
              <h5 className="card-title">Total Investment</h5>
              <h3>₹{totalInvestment.toLocaleString()}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-info text-white">
            <div className="card-body">
              <h5 className="card-title">This Month EMI</h5>
              <h3>₹{calculateThisMonthEMI().toLocaleString()}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-warning text-dark">
            <div className="card-body">
              <h5 className="card-title">Active EMI Plans</h5>
              <h3>{emiPlans.filter(emi => emi.status === 'active').length}</h3>
            </div>
          </div>
        </div>
      </div>
      
      {/* Tab Navigation */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Inventory Management</h4>
        <div>
          <button 
            className={`btn ${view === 'products' ? 'btn-primary' : 'btn-outline-primary'} me-2`}
            onClick={() => setView('products')}
          >
            Products
          </button>
          <button 
            className={`btn ${view === 'emi' ? 'btn-primary' : 'btn-outline-primary'} me-2`}
            onClick={() => setView('emi')}
          >
            EMI Payments
          </button>
          <button 
            className={`btn ${view === 'sales' ? 'btn-primary' : 'btn-outline-primary'} me-2`}
            onClick={() => setView('sales')}
          >
            Sales
          </button>
          <Link to="/" className="btn btn-info me-2">Grid View</Link>
          <Link to="/add-product" className="btn btn-primary">Add Product</Link>
        </div>
      </div>

      {view === 'products' ? (
        <>
          <div className="card mb-4">
            <div className="card-body">
              <h5 className="card-title">Inventory Summary</h5>
              <div className="row g-3">
                <div className="col-md-3">
                  <div className="border rounded p-3 text-center bg-light">
                    <h6><i className="bi bi-box-seam me-2"></i>Total Products</h6>
                    <h3 className="text-primary">{products.length}</h3>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="border rounded p-3 text-center bg-light">
                    <h6><i className="bi bi-boxes me-2"></i>Total Stock</h6>
                    <h3 className="text-info">{products.reduce((sum, product) => sum + (product.stock || 0), 0)}</h3>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="border rounded p-3 text-center bg-light">
                    <h6><i className="bi bi-wallet2 me-2"></i>Total Investment</h6>
                    <h3 className="text-success">₹{calculateTotalInvestment().toLocaleString()}</h3>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="border rounded p-3 text-center bg-light">
                    <h6><i className="bi bi-graph-up-arrow me-2"></i>Potential Revenue</h6>
                    <h3 className="text-warning">₹{products.reduce((sum, product) => 
                      sum + ((product.price || 0) * (product.stock || 0)), 0).toLocaleString()}</h3>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h5 className="card-title">Category Distribution</h5>
              <div className="row g-2">
                <div className="col-md-2">
                  <button className="border rounded p-2 text-center bg-warning bg-opacity-25 w-100" onClick={() => setCategoryFilter('Mobile')}>
                    <small className="text-muted">📱 Mobile</small>
                    <h4 className="text-primary mb-0">{products.filter(p => p.category === 'Mobile').length}</h4>
                    <small>Items: {products.filter(p => p.category === 'Mobile').reduce((sum, p) => sum + (p.stock || 0), 0)}</small>
                  </button>
                </div>
                <div className="col-md-2">
                  <button className="border rounded p-2 text-center bg-warning bg-opacity-50 w-100" onClick={() => setCategoryFilter('Old Mobile')}>
                    <small className="text-muted">📲 Old Mobile</small>
                    <h4 className="text-primary mb-0">{products.filter(p => p.category === 'Old Mobile').length}</h4>
                    <small>Items: {products.filter(p => p.category === 'Old Mobile').reduce((sum, p) => sum + (p.stock || 0), 0)}</small>
                  </button>
                </div>
                <div className="col-md-2">
                  <button className="border rounded p-2 text-center bg-info bg-opacity-25 w-100" onClick={() => setCategoryFilter('Speaker')}>
                    <small className="text-muted">🔊 Speaker</small>
                    <h4 className="text-info mb-0">{products.filter(p => p.category === 'Speaker').length}</h4>
                    <small>Items: {products.filter(p => p.category === 'Speaker').reduce((sum, p) => sum + (p.stock || 0), 0)}</small>
                  </button>
                </div>
                <div className="col-md-2">
                  <button className="border rounded p-2 text-center bg-success bg-opacity-25 w-100" onClick={() => setCategoryFilter('Neckband')}>
                    <small className="text-muted">🎧 Neckband</small>
                    <h4 className="text-success mb-0">{products.filter(p => p.category === 'Neckband').length}</h4>
                    <small>Items: {products.filter(p => p.category === 'Neckband').reduce((sum, p) => sum + (p.stock || 0), 0)}</small>
                  </button>
                </div>
                <div className="col-md-2">
                  <button className="border rounded p-2 text-center bg-primary bg-opacity-25 w-100" onClick={() => setCategoryFilter('Airpods')}>
                    <small className="text-muted">🎵 Airpods</small>
                    <h4 className="text-primary mb-0">{products.filter(p => p.category === 'Airpods').length}</h4>
                    <small>Items: {products.filter(p => p.category === 'Airpods').reduce((sum, p) => sum + (p.stock || 0), 0)}</small>
                  </button>
                </div>
                <div className="col-md-2">
                  <button className="border rounded p-2 text-center bg-secondary bg-opacity-25 w-100" onClick={() => setCategoryFilter('Smartwatches')}>
                    <small className="text-muted">⌚ Smartwatches</small>
                    <h4 className="text-secondary mb-0">{products.filter(p => p.category === 'Smartwatches').length}</h4>
                    <small>Items: {products.filter(p => p.category === 'Smartwatches').reduce((sum, p) => sum + (p.stock || 0), 0)}</small>
                  </button>
                </div>
                <div className="col-md-2">
                  <button className="border rounded p-2 text-center bg-danger bg-opacity-25 w-100" onClick={() => setCategoryFilter('TV')}>
                    <small className="text-muted">📺 TV</small>
                    <h4 className="text-danger mb-0">{products.filter(p => p.category === 'TV').length}</h4>
                    <small>Items: {products.filter(p => p.category === 'TV').reduce((sum, p) => sum + (p.stock || 0), 0)}</small>
                  </button>
                </div>
                <div className="col-md-2">
                  <button className="border rounded p-2 text-center bg-info bg-opacity-25 w-100" onClick={() => setCategoryFilter('Refrigerator')}>
                    <small className="text-muted">❄️ Refrigerator</small>
                    <h4 className="text-info mb-0">{products.filter(p => p.category === 'Refrigerator').length}</h4>
                    <small>Items: {products.filter(p => p.category === 'Refrigerator').reduce((sum, p) => sum + (p.stock || 0), 0)}</small>
                  </button>
                </div>
                <div className="col-md-2">
                  <button className="border rounded p-2 text-center bg-warning bg-opacity-25 w-100" onClick={() => setCategoryFilter('AC')}>
                    <small className="text-muted">❄️ AC</small>
                    <h4 className="text-warning mb-0">{products.filter(p => p.category === 'AC').length}</h4>
                    <small>Items: {products.filter(p => p.category === 'AC').reduce((sum, p) => sum + (p.stock || 0), 0)}</small>
                  </button>
                </div>
                <div className="col-md-2">
                  <button className="border rounded p-2 text-center bg-success bg-opacity-25 w-100" onClick={() => setCategoryFilter('FAN')}>
                    <small className="text-muted">💨 FAN</small>
                    <h4 className="text-success mb-0">{products.filter(p => p.category === 'FAN').length}</h4>
                    <small>Items: {products.filter(p => p.category === 'FAN').reduce((sum, p) => sum + (p.stock || 0), 0)}</small>
                  </button>
                </div>
                <div className="col-md-2">
                  <button className="border rounded p-2 text-center bg-dark bg-opacity-25 w-100" onClick={() => setCategoryFilter('Other Accessories')}>
                    <small className="text-muted">🔧 Other Accessories</small>
                    <h4 className="text-dark mb-0">{products.filter(p => p.category === 'Other Accessories').length}</h4>
                    <small>Items: {products.filter(p => p.category === 'Other Accessories').reduce((sum, p) => sum + (p.stock || 0), 0)}</small>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="card-title mb-0">Filter Products</h5>
              </div>
              
              {/* Product Search Bar */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label">🔍 Search Products</label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by product name, category, or description..."
                      value={productSearchTerm}
                      onChange={(e) => setProductSearchTerm(e.target.value)}
                    />
                    {productSearchTerm && (
                      <button 
                        className="btn btn-outline-secondary" 
                        type="button"
                        onClick={() => setProductSearchTerm('')}
                      >
                        <i className="bi bi-x"></i>
                      </button>
                    )}
                  </div>
                  <div className="form-text">
                    Found {getFilteredProducts().length} of {products.length} products
                  </div>
                </div>
              </div>
              
              <div className="row">
                <div className="col-md-4">
                  <label className="form-label">Category</label>
                  <select 
                    className="form-select" 
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                  >
                    <option value="all">🏪 All Categories</option>
                    <option value="Mobile">📱 Mobile</option>
                    <option value="Old Mobile">📲 Old Mobile</option>
                    <option value="Speaker">🔊 Speaker</option>
                    <option value="Neckband">🎧 Neckband</option>
                    <option value="Airpods">🎵 Airpods</option>
                    <option value="Smartwatches">⌚ Smartwatches</option>
                    <option value="TV">📺 TV</option>
                    <option value="Refrigerator">❄️ Refrigerator</option>
                    <option value="AC">❄️ AC</option>
                    <option value="FAN">💨 FAN</option>
                    <option value="Other Accessories">🔧 Other Accessories</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Stock Status</label>
                  <select 
                    className="form-select"
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                  >
                    <option value="all">All Products</option>
                    <option value="low">Low Stock (≤ 3)</option>
                    <option value="medium">Medium Stock (4-10)</option>
                    <option value="high">High Stock (&gt; 10)</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Sort By</label>
                  <select 
                    className="form-select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="latest">🕒 Latest First</option>
                    <option value="oldest">🕒 Oldest First</option>
                    <option value="priceLowToHigh">💰 Price: Low to High</option>
                    <option value="priceHighToLow">💰 Price: High to Low</option>
                    <option value="stockLowToHigh">📦 Stock: Low to High</option>
                    <option value="stockHighToLow">📦 Stock: High to Low</option>
                    <option value="nameAZ">📝 Name: A to Z</option>
                    <option value="nameZA">📝 Name: Z to A</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-striped table-bordered">
              <thead className="table-dark">
                <tr>
                  <th>Product Name</th>
                  <th>Category</th>
                  <th>Grade</th>
                  <th>Buying Price</th>
                  <th>Selling Price</th>
                  <th>Offer Price</th>
                  <th>Profit Margin</th>
                  <th>Stock</th>
                  <th>Invested Amount</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {getFilteredProducts().map(product => {
                  // Add null checks to avoid errors
                  const buyingPrice = product.buyingPrice || 0;
                  const sellingPrice = product.price || 0;
                  const stock = product.stock || 0;
                  const profitMargin = ((sellingPrice - buyingPrice) / buyingPrice * 100).toFixed(2);
                  const stockClass = stock > 10 ? 'text-success' : (stock > 3 ? 'text-warning' : 'text-danger');
                  
                  return (
                    <tr key={product._id} className={product.category === 'Mobile' ? 'table-warning' : ''}>
                      <td>
                        <strong>{product.name}</strong>
                      </td>
                      <td>
                        <span className="me-1">{getCategoryIcon(product.category)}</span>
                        {product.category}
                      </td>
                      <td>
                        {product.category === 'Old Mobile' ? (() => {
                          const grades = formatOldMobileGradesSummary(product);
                          if (!grades) return <span className="text-muted">—</span>;
                          return grades.split(', ').map((g) => (
                            <span key={g} className={`badge ${gradeBadgeClass(g)} me-1`}>
                              {g}
                            </span>
                          ));
                        })() : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>₹{buyingPrice.toLocaleString()}</td>
                      <td>₹{sellingPrice.toLocaleString()}</td>
                      <td>{product.offerPrice ? `₹${product.offerPrice.toLocaleString()}` : '-'}</td>
                      <td className={Number(profitMargin) > 15 ? 'text-success' : 'text-danger'}>
                        {profitMargin}%
                      </td>
                      <td className={stockClass}>{stock}</td>
                      <td>₹{(buyingPrice * stock).toLocaleString()}</td>
                      <td>
                        <div className="btn-group">
                          <Link to={`/product/${product._id}`} className="btn btn-sm btn-info" title="View Details">
                            <i className="bi bi-eye"></i>
                          </Link>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => handleEditProduct(product)}
                            title="Edit Product"
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleAddVariant(product)}
                            title="Add Variant"
                          >
                            <i className="bi bi-plus-square"></i>
                          </button>
                          <button 
                            className="btn btn-sm btn-success" 
                            onClick={() => handleIncrementStock(product._id)}
                            title="Add Stock"
                          >
                            <i className="bi bi-plus-circle"></i>
                          </button>
                          <button 
                            className="btn btn-sm btn-warning" 
                            onClick={() => handleRecordSale(product._id, product.price)}
                            title="Record Sale"
                          >
                            <i className="bi bi-cart-plus"></i>
                          </button>
                          {(product.category === 'Mobile' || product.category === 'Old Mobile') &&
                            product.imeiTracking &&
                            product.imeiTracking.length > 0 && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleOpenImeiManagement(product._id)}
                              title="IMEI-wise: available / sold — customer & date"
                            >
                              <i className="bi bi-phone"></i>
                            </button>
                          )}
                          {!(
                            (product.category === 'Mobile' || product.category === 'Old Mobile') &&
                            product.imeiTracking &&
                            product.imeiTracking.length > 0
                          ) && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-info"
                              onClick={() =>
                                setSaleHistoryModal({ productId: product._id, productName: product.name })
                              }
                              title="Sale history — kis customer ko kab becha"
                            >
                              <i className="bi bi-clock-history"></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : view === 'emi' ? (
        <>
          <div className="card mb-4">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="card-title mb-0">EMI Summary</h5>
                <Link to="/emi-dashboard" className="btn btn-primary">
                  📊 View EMI Dashboard
                </Link>
              </div>
              <div className="row">
                <div className="col-md-3">
                  <div className="border rounded p-3 text-center">
                    <h6>Active EMI Plans</h6>
                    <h3>{emiPlans.filter(emi => emi.status === 'active').length}</h3>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="border rounded p-3 text-center">
                    <h6>Pending Amount</h6>
                    <h3>₹{calculatePendingEmiAmount().toLocaleString()}</h3>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="border rounded p-3 text-center">
                    <h6>This Month's EMI</h6>
                    <h3>₹{calculateThisMonthEMI().toLocaleString()}</h3>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="border rounded p-3 text-center">
                    <h6>Total Reward Points</h6>
                    <h3 className="text-success">{emiPlans.reduce((total, emi) => total + calculateRewardPoints(calculateEmiProfit(emi)), 0).toLocaleString()}</h3>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Customer Search Bar */}
          <div className="card mb-4">
            <div className="card-body">
              <form onSubmit={handleEmiSearch} className="row g-3 align-items-center">
                <div className="col-md-6">
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search customer by phone or name..."
                      value={emiSearchTerm}
                      onChange={(e) => setEmiSearchTerm(e.target.value)}
                    />
                    <button 
                      className="btn btn-primary" 
                      type="submit"
                      disabled={isEmiSearching}
                    >
                      {isEmiSearching ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Searching...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-search me-1"></i> Search
                        </>
                      )}
                    </button>
                    {emiSearchTerm && (
                      <button 
                        className="btn btn-outline-secondary" 
                        type="button"
                        onClick={clearEmiSearch}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <div className="form-text">
                    Enter customer phone number or name to find their EMI plans
                  </div>
                </div>
                <div className="col-md-6 d-flex justify-content-end">
                  <div className="d-flex gap-2">
                    <select 
                      className="form-select" 
                      value={filterType}
                      onChange={(e) => setFilterType(e.target.value)}
                    >
                      <option value="all">All EMI Plans</option>
                      <option value="pending">Active Plans</option>
                      <option value="completed">Completed Plans</option>
                      <option value="defaulted">Defaulted Plans</option>
                    </select>
                    <select 
                      className="form-select" 
                      value={monthFilter}
                      onChange={(e) => setMonthFilter(e.target.value)}
                    >
                      <option value="all">All Months</option>
                      {availableMonths.map(month => (
                        <option key={month.key} value={month.value}>
                          {month.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </form>
            </div>
          </div>
          
          <div className="card">
            <div className="card-body">
              {getFilteredEmiPlans().length === 0 ? (
                <div className="alert alert-info">
                  {emiSearchTerm ? `No EMI plans found for "${emiSearchTerm}"` : "No EMI plans found with the selected filters"}
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped table-bordered">
                    <thead className="table-dark">
                      <tr>
                        <th>Date</th>
                        <th>Customer Name</th>
                        <th>Model</th>
                        <th>Variant</th>
                        <th>Sold Price (₹)</th>
                        <th>Down Payment (₹)</th>
                        <th>EMI Tenure</th>
                        <th>Monthly EMI (₹)</th>
                        <th>Late Fees (₹)</th>
                        <th>Given EMI (₹)</th>
                        <th>Pending Amount (₹)</th>
                        <th>Reward Points</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredEmiPlans().map(emi => {
                        const productName = emi.productInfo?.name || 'Unknown Product';
                        const productVariant = emi.productInfo?.variant || '-';
                        const salePrice = emi.saleInfo?.salePrice || emi.productInfo?.sellingPrice || 0;
                        const profit = calculateEmiProfit(emi);
                        const rewardPoints = calculateRewardPoints(profit);
                        
                        // Calculate payments correctly
                        const downPayment = emi.emiPlan.downPayment || 0;
                        const totalEmiAmount = emi.emiPlan.totalAmount - downPayment; // Total EMI amount excluding down payment
                        
                        // Calculate total paid (excluding down payment for "Given EMI" column)
                        const totalPaid = emi.payments.reduce((total, payment, index) => {
                          // Only count completed payments
                          if (payment.status === 'completed') {
                            // Skip the first payment (down payment) for "Given EMI" column
                            if (index === 0) {
                              return total; // Don't add down payment to "Given EMI"
                            }
                            return total + payment.amount;
                          }
                          return total;
                        }, 0);
                        
                        // Calculate pending amount (total amount - all payments including down payment)
                        const allPayments = emi.payments.reduce((total, payment) => 
                          payment.status === 'completed' ? total + payment.amount : total, 0);
                        const pendingAmount = emi.emiPlan.totalAmount - allPayments;
                        
                        const lateFee = calculateLateFee(emi);
                        const startDate = new Date(emi.startDate);
                        
                        return (
                          <tr key={emi._id}>
                            <td>{startDate.toISOString().split('T')[0]}</td>
                            <td>{emi.customer.name}</td>
                            <td>{productName}</td>
                            <td>{productVariant}</td>
                            <td>₹{salePrice.toLocaleString()}</td>
                            <td>₹{emi.emiPlan.downPayment.toLocaleString()}</td>
                            <td>{emi.emiPlan.months} months</td>
                            <td>₹{emi.emiPlan.monthlyAmount.toFixed(0)}</td>
                            <td>₹{lateFee.toLocaleString()}</td>
                            <td>₹{totalPaid.toLocaleString()}</td>
                            <td>₹{pendingAmount.toLocaleString()}</td>
                            <td>{rewardPoints} points</td>
                            <td>
                              <div className="btn-group">
                                <Link 
                                  to={`/emi/${emi._id}`}
                                  className="btn btn-sm btn-info"
                                >
                                  View
                                </Link>
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => handleRecordPayment(emi._id)}
                                  disabled={emi.status === 'completed'}
                                >
                                  Pay
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        // Sales View
        <>
          <div className="card mb-4">
            <div className="card-body">
              <h5 className="card-title">Sales Summary</h5>
              <div className="row">
                <div className="col-md-3">
                  <div className="border rounded p-3 text-center">
                    <h6>Total Sales</h6>
                    <h3>{sales.length}</h3>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="border rounded p-3 text-center">
                    <h6>Total Revenue</h6>
                    <h3>₹{sales.reduce((sum, sale) => 
                      sum + ((sale.salePrice || 0) * (sale.quantity || 1)), 0).toLocaleString()}</h3>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="border rounded p-3 text-center">
                    <h6>Total Profit</h6>
                    <h3>₹{sales.reduce((sum, sale) => 
                      sum + (((sale.salePrice || 0) - (sale.buyingPrice || 0)) * (sale.quantity || 1)), 0).toLocaleString()}</h3>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="border rounded p-3 text-center">
                    <h6>Total Items Sold</h6>
                    <h3>{sales.reduce((sum, sale) => sum + (sale.quantity || 1), 0)}</h3>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="card-title mb-0">Filter Sales</h5>
              </div>
              <div className="row">
                <div className="col-md-4">
                  <label className="form-label">Month</label>
                  <select 
                    className="form-select"
                    value={monthFilter}
                    onChange={(e) => setMonthFilter(e.target.value)}
                  >
                    <option value="all">All Months</option>
                    {availableMonths.map(month => (
                      <option key={month.key} value={month.value}>
                        {month.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Category</label>
                  <select 
                    className="form-select" 
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                  >
                    <option value="all">🏪 All Categories</option>
                    <option value="Mobile">📱 Mobile</option>
                    <optgroup label="🎧 Audio Accessories">
                      {categories.filter(cat => ['Bluetooth Speaker', 'Neckband', 'Headphones', 'Airpods'].includes(cat)).map((category, index) => (
                        <option key={index} value={category}>
                          {category === 'Bluetooth Speaker' ? '🔊' : '🎧'} {category}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="⚡ Power & Charging">
                      {categories.filter(cat => ['Power Bank', 'Charger', 'Cable'].includes(cat)).map((category, index) => (
                        <option key={index} value={category}>
                          {category === 'Power Bank' ? '🔋' : '⚡'} {category}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="📱 Phone Accessories">
                      {categories.filter(cat => ['Phone Case', 'Screen Guard', 'Memory Card'].includes(cat)).map((category, index) => (
                        <option key={index} value={category}>
                          {category === 'Phone Case' ? '📱' : category === 'Screen Guard' ? '🛡️' : '💾'} {category}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="🔧 Other Items">
                      {categories.filter(cat => ['Smartwatch', 'Accessories', 'Other'].includes(cat)).map((category, index) => (
                        <option key={index} value={category}>
                          {category === 'Smartwatch' ? '⌚' : '🔧'} {category}
                        </option>
                      ))}
                      {categories.filter(cat => !['Mobile', 'Bluetooth Speaker', 'Neckband', 'Headphones', 'Airpods', 'Power Bank', 'Charger', 'Cable', 'Phone Case', 'Screen Guard', 'Memory Card', 'Smartwatch', 'Accessories', 'Other'].includes(cat)).map((category, index) => (
                        <option key={`other-${index}`} value={category}>📦 {category}</option>
                      ))}
                    </optgroup>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {view === 'sales' && (
            <>
              {/* Monthly sales summary */}
              <div className="card mb-4">
                <div className="card-header">
                  <h5 className="mb-0">Monthly Sales Summary</h5>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>Month</th>
                          <th>Total Sales</th>
                          <th>Revenue</th>
                          <th>Profit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(getFilteredSales() || []).map(monthData => (
                          <tr key={monthData.month}>
                            <td>{monthData.month}</td>
                            <td>{monthData.totalSales}</td>
                            <td>₹{(monthData.totalRevenue || 0).toLocaleString()}</td>
                            <td className={(monthData.totalProfit || 0) > 0 ? 'text-success' : 'text-danger'}>
                              ₹{(monthData.totalProfit || 0).toLocaleString()}
                            </td>
                          </tr>
                        ))}
                        {(getFilteredSales() || []).length === 0 && (
                          <tr>
                            <td colSpan="4" className="text-center">No sales data for this period</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              
              {/* Recent sales list */}
              <div className="card">
                <div className="card-header">
                  <h5 className="mb-0">Recent Sales</h5>
                </div>
                <div className="card-body">
                  {/* This table was also buggy, ensuring it's safe now */}
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Product</th>
                          <th>Sale Price</th>
                          <th>Customer</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(sales || []).slice(0, 20).map(sale => (
                          <tr key={sale._id}>
                            <td>{new Date(sale.date).toLocaleDateString()}</td>
                            <td>{sale.productName}</td>
                            <td>₹{(sale.salePrice || 0).toLocaleString()}</td>
                            <td>{sale.customerName || 'N/A'}</td>
                          </tr>
                        ))}
                        {(sales || []).length === 0 && (
                          <tr>
                            <td colSpan="4" className="text-center">No recent sales</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* Payment Modal */}
      <PaymentModal
        show={showPaymentModal}
        emiPlans={emiPlans}
        selectedEmiId={selectedEmiId}
        paymentAmount={paymentAmount}
        paymentLoading={paymentLoading}
        calculateLateFee={calculateLateFee}
        onAmountChange={setPaymentAmount}
        onSubmit={submitPayment}
        onClose={() => setShowPaymentModal(false)}
      />

      {/* Stock Increment Modal */}
      <StockModal
        show={showStockModal}
        products={products}
        selectedProductId={selectedProductId}
        stockAmount={stockAmount}
        imeiList={imeiList}
        stockLoading={stockLoading}
        onStockAmountChange={(val, newList) => { setStockAmount(val); setImeiList(newList); }}
        onImeiListChange={setImeiList}
        onSubmit={submitStockIncrement}
        onClose={() => { setShowStockModal(false); setImeiList([]); setStockAmount(''); }}
      />

      {/* Sales Recording Modal */}
      <SaleModal
        show={showSaleModal}
        saleData={saleData}
        products={products}
        selectedProductId={selectedProductId}
        selectedCustomer={selectedCustomer}
        customerSearchResults={customerSearchResults}
        customerSearchLoading={customerSearchLoading}
        showNewCustomerForm={showNewCustomerForm}
        newCustomer={newCustomer}
        isSelfWitness={isSelfWitness}
        selectedWitness={selectedWitness}
        witnessSearchTerm={witnessSearchTerm}
        witnessSearchResults={witnessSearchResults}
        isWitnessSearching={isWitnessSearching}
        error={error}
        submittingSale={submittingSale}
        onSaleDataChange={setSaleData}
        onSearchCustomer={searchCustomerByPhone}
        onSelectCustomer={handleSelectCustomer}
        onToggleNewCustomerForm={() => setShowNewCustomerForm(!showNewCustomerForm)}
        onNewCustomerChange={handleNewCustomerChange}
        onSetIsSelfWitness={setIsSelfWitness}
        onSetSelectedWitness={setSelectedWitness}
        onWitnessSearchChange={(val) => { setWitnessSearchTerm(val); searchWitnesses(val); }}
        onClearWitnessSearch={clearWitnessSearch}
        salePhotoFile={salePhotoFile}
        onSalePhotoChange={setSalePhotoFile}
        onSubmit={submitSaleRecord}
        onClose={() => { setShowSaleModal(false); setSalePhotoFile(null); }}
      />

            {/* Advanced Edit Product Modal */}
      <AdvancedEditModal
        show={showEditModal}
        product={editProductData}
        onClose={() => setShowEditModal(false)}
        onSave={handleUpdateProduct}
        loading={editLoading}
      />

      {/* Variant Modal */}
      <VariantModal
        show={showVariantModal}
        variantData={variantData}
        variantLoading={variantLoading}
        onDataChange={setVariantData}
        onSubmit={handleAddVariantSubmit}
        onClose={() => setShowVariantModal(false)}
      />

      {/* IMEI Management Modal */}
      <ImeiManagementModal
        show={showImeiManagementModal}
        products={products}
        selectedImeiProductId={selectedImeiProductId}
        imeiManagementLoading={imeiManagementLoading}
        onSetQcViewer={setUnitQcViewer}
        onMarkSold={handleMarkImeiAsSold}
        onBulkMarkSold={handleBulkMarkImeisAsSold}
        onOpenBuyback={openOldMobileBuyback}
        onRepurchaseSale={handleRecordSale}
        onClose={() => { setShowImeiManagementModal(false); setSelectedImeiProductId(null); }}
      />

      <BuybackModal
        buybackModal={buybackModal}
        buybackPriceInput={buybackPriceInput}
        buybackNotesInput={buybackNotesInput}
        buybackSubmitting={buybackSubmitting}
        onPriceChange={setBuybackPriceInput}
        onNotesChange={setBuybackNotesInput}
        onSubmit={submitOldMobileBuyback}
        onClose={() => setBuybackModal(null)}
      />

      <ProductSaleHistoryModal
        show={Boolean(saleHistoryModal)}
        productId={saleHistoryModal?.productId}
        productName={saleHistoryModal?.productName}
        onClose={() => setSaleHistoryModal(null)}
      />

      <UnitQcViewerModal
        unitQcViewer={unitQcViewer}
        onClose={() => setUnitQcViewer(null)}
      />
    </div>
  );
}

export default Inventory; 
